import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import LeadStreamsService from 'src/services/LeadStreamsService';
import EventService from 'src/services/EventService';
import useUserStore from 'src/stores/user';
import useSessionStore from 'src/stores/session';
import { clearConfirmedSelection, clearPackageCheckout, readPackageCheckout } from 'src/utils/packageCheckout';
import { flushProductFunnel, recordProductFunnel, rotateProductFunnelAttempt } from 'src/services/ProductFunnel';

import type { LeadPackagePurchase } from 'src/services/LeadStreamsService/agent';

export default function PaymentReturnNotice() {
    const { search, pathname } = useLocation();
    const history = useHistory();
    const owner = useUserStore((user) => user.id);
    const queryClient = useQueryClient();
    const [checkout] = useState(() => readPackageCheckout(owner));
    const requested = new URLSearchParams(search).get('payment_success') === 'true';
    const [dismissed, setDismissed] = useState(false);
    const [state, setState] = useState<'checking' | 'waiting' | 'paid' | 'cancelled' | 'error' | 'unknown'>('checking');
    const [rows, setRows] = useState<LeadPackagePurchase[]>([]);
    const [attempt, setAttempt] = useState(0);
    const visible = !dismissed && (requested || Boolean(checkout));
    useEffect(() => { if (owner) void flushProductFunnel(); }, [owner]);
    useEffect(() => {
        const streamId = checkout?.purchases[0]?.streamId;
        if (requested && visible && streamId) {
            void recordProductFunnel('payment_returned', {
                stream_id: streamId, return_state: 'returned',
            }, `payment_returned:${streamId}`);
        }
    }, [requested, visible, checkout]);
    useEffect(() => {
        if (!visible) return;
        if (!checkout) {
            setState('unknown');
            return;
        }
        let live = true,
            rounds = 0;
        let timer: ReturnType<typeof setTimeout>;
        setState('checking');
        const check = async () => {
            try {
                const purchases = await Promise.all(
                    checkout.purchases.map((p) => LeadStreamsService.getPurchase(p.id))
                );
                if (!live) return;
                if (purchases.some((p, i) => p.id !== checkout.purchases[i].id || p.user !== owner))
                    throw new Error('Purchase mismatch');
                setRows(purchases);
                if (purchases.every((p) => p.status === 'paid')) {
                    setState('paid');
                    rotateProductFunnelAttempt();
                    clearConfirmedSelection(checkout);
                    EventService.dispatch('LEAD_STREAMS_UPDATED');
                    void queryClient.invalidateQueries(LeadStreamsService.queryKey);
                    void queryClient.invalidateQueries('fetch-message');
                    void useSessionStore
                        .getState()
                        .getUserSessionRequiredData()
                        .catch(() => undefined);
                    if (checkout.purchases.length === 1) {
                        const streamId = checkout.purchases[0].streamId;
                        clearPackageCheckout(owner);
                        history.replace(`/leadstream/${streamId}#training`);
                    }
                    return;
                }
                if (purchases.every((p) => ['paid', 'cancelled', 'canceled', 'failed'].includes(p.status))) {
                    setState('cancelled');
                    return;
                }
                setState('waiting');
                if (++rounds < 8) timer = setTimeout(check, 5000);
            } catch {
                if (live) setState('error');
            }
        };
        void check();
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [visible, checkout, attempt, owner, queryClient, history]);
    if (!visible) return null;
    const dismiss = () => {
        if (state === 'paid' || state === 'cancelled') clearPackageCheckout(owner);
        setDismissed(true);
        const params = new URLSearchParams(search);
        params.delete('payment_success');
        history.replace(pathname + (params.toString() ? '?' + params.toString() : ''));
    };
    const copy = {
        checking: 'Проверяем пополнение…',
        waiting: 'Ждём подтверждение оплаты',
        paid: 'Пополнение подтверждено',
        cancelled: rows.some((row) => row.status === 'paid') ? 'Часть пакетов не оплачена' : 'Оплата не завершена',
        error: 'Не удалось проверить оплату',
        unknown: 'Проверьте пополнение потока',
    };
    return (
        <Alert
            severity={state === 'paid' ? 'success' : state === 'error' ? 'warning' : 'info'}
            icon={state === 'checking' ? <CircularProgress size={18} /> : false}
            onClose={dismiss}
            closeText="Скрыть"
            sx={{ mb: 2 }}
        >
            <Typography fontWeight={600}>{copy[state]}</Typography>
            {state === 'unknown' && (
                <Typography variant="body2">
                    Возврат со страницы оплаты ещё не подтверждает зачисление. Откройте нужный поток и проверьте баланс.
                </Typography>
            )}
            {state === 'waiting' && (
                <Typography variant="body2">Баланс обновится после подтверждения платёжной системы.</Typography>
            )}
            {state === 'error' && <Typography variant="body2">Повторная проверка не создаёт новый платёж.</Typography>}
            <Stack
                spacing={1}
                mt={1}
            >
                {checkout?.purchases.map((purchase) => {
                    const row = rows.find((p) => p.id === purchase.id);
                    const paid = row?.status === 'paid';
                    const pending = row && !paid && !['cancelled', 'canceled', 'failed'].includes(row.status);
                    return (
                        <Box key={purchase.id}>
                            {row && (
                                <Typography variant="body2">
                                    {row.category?.name || 'Пакет лидов'} ·{' '}
                                    {paid
                                        ? 'Зачислено ' + (row.credited_package_size || row.package_size) + ' лидов'
                                        : pending
                                        ? 'Ожидает оплаты'
                                        : 'Не оплачен'}
                                </Typography>
                            )}
                            {paid ? (
                                <Button
                                    component={Link}
                                    to={'/leadstream/' + purchase.streamId}
                                    size="small"
                                >
                                    Открыть поток
                                </Button>
                            ) : pending && purchase.confirmationUrl ? (
                                <Button
                                    component="a"
                                    href={purchase.confirmationUrl}
                                    size="small"
                                >
                                    Продолжить оплату
                                </Button>
                            ) : null}
                        </Box>
                    );
                })}
                {['waiting', 'error'].includes(state) && (
                    <Button
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() => setAttempt((n) => n + 1)}
                    >
                        Проверить оплату
                    </Button>
                )}
            </Stack>
        </Alert>
    );
}
